import { inject, Injectable, InjectionToken } from '@angular/core';
import { combineLatest, EMPTY, Observable, of, from, Subject, firstValueFrom } from 'rxjs';
import { catchError, filter, map, skip, switchMap, take, tap } from 'rxjs/operators';
import { type CaseRepository, CaseSortCriteria, CaseListFilter, CaseSearchContext } from '@telespot/domain';
import {
  type ICountWorkspaceCasesQuery,
  type IGetCaseAnalysisSummaryQuery,
  type IListWorkspaceCasesQuery,
  ListedWorkspaceCase,
} from '@telespot/management/use-cases';
import { MoveCases, type IMoveCasesHandler, CopyCases, type ICopyCasesHandler } from '@telespot/management/use-cases';
import { CloudFunctions } from '@telespot/sdk';
import { concatLatestFrom } from '@ngrx/operators';
import { PaginatedStateEffects } from '@shared/ui';
import {
  WorkspaceCasesState,
  WorkspaceCaseData,
  WorkspaceCasesStateModel,
  WorkspaceCaseState,
} from './workspace-cases.state';

export const WORKSPACE_CASES_CASE_REPOSITORY = new InjectionToken<CaseRepository>('WORKSPACE_CASES_CASE_REPOSITORY');
export const LIST_WORKSPACE_CASES_QUERY = new InjectionToken<IListWorkspaceCasesQuery>('LIST_WORKSPACE_CASES_QUERY');
export const COUNT_WORKSPACE_CASES_QUERY = new InjectionToken<ICountWorkspaceCasesQuery>('COUNT_WORKSPACE_CASES_QUERY');
export const GET_CASE_ANALYSIS_SUMMARY_QUERY = new InjectionToken<IGetCaseAnalysisSummaryQuery>(
  'GET_CASE_ANALYSIS_SUMMARY_QUERY'
);
export const WORKSPACE_CASES_MOVE_HANDLER = new InjectionToken<IMoveCasesHandler>('WORKSPACE_CASES_MOVE_HANDLER');
export const WORKSPACE_CASES_COPY_HANDLER = new InjectionToken<ICopyCasesHandler>('WORKSPACE_CASES_COPY_HANDLER');

@Injectable()
export class WorkspaceCasesEffects extends PaginatedStateEffects<WorkspaceCaseData, WorkspaceCasesStateModel> {
  /**
   * Emits once each time a case CRUD operation (delete, move, copy) succeeds.
   * Lets the facade refresh the workspace detail counters without coupling
   * effects classes to each other.
   */
  private readonly _caseOperationCompleted$ = new Subject<void>();
  public readonly caseOperationCompleted$ = this._caseOperationCompleted$.asObservable();

  private readonly caseRepository = inject<CaseRepository>(WORKSPACE_CASES_CASE_REPOSITORY);
  private readonly listWorkspaceCasesQuery = inject<IListWorkspaceCasesQuery>(LIST_WORKSPACE_CASES_QUERY);
  private readonly countWorkspaceCasesQuery = inject<ICountWorkspaceCasesQuery>(COUNT_WORKSPACE_CASES_QUERY);
  private readonly analysisSummaryQuery = inject<IGetCaseAnalysisSummaryQuery>(GET_CASE_ANALYSIS_SUMMARY_QUERY);
  private readonly moveCasesHandler = inject<IMoveCasesHandler>(WORKSPACE_CASES_MOVE_HANDLER);
  private readonly copyCasesHandler = inject<ICopyCasesHandler>(WORKSPACE_CASES_COPY_HANDLER);

  constructor(protected override readonly paginatedState: WorkspaceCasesState) {
    super(paginatedState);

    this.initializeCases(this.paginatedState.workspaceId$);
    this.onSortOrFilterChange(
      combineLatest([this.paginatedState.sortCriteria$, this.paginatedState.filters$]).pipe(skip(1))
    );
  }

  // ============================================================================
  // Custom selectors — re-exposed from WorkspaceCasesState
  // ============================================================================

  public get workspaceId$() {
    return this.paginatedState.workspaceId$;
  }
  public get sortCriteria$() {
    return this.paginatedState.sortCriteria$;
  }
  public get filters$() {
    return this.paginatedState.filters$;
  }
  public get movingCase$() {
    return this.paginatedState.movingCase$;
  }
  public get copyingCase$() {
    return this.paginatedState.copyingCase$;
  }
  public get encrypted$() {
    return this.paginatedState.encrypted$;
  }
  public get nameEncrypted$() {
    return this.paginatedState.nameEncrypted$;
  }
  public get decryptingCases$() {
    return this.paginatedState.decryptingCases$;
  }
  public get canDecrypt$() {
    return this.paginatedState.canDecrypt$;
  }
  public get operationError$() {
    return this.paginatedState.operationError$;
  }
  public get cases$() {
    return this.paginatedState.cases$;
  }
  public get totalCaseCount$() {
    return this.paginatedState.totalCaseCount$;
  }
  public get decrypted$() {
    return this.paginatedState.decrypted$;
  }
  public get userDecrypted$() {
    return this.paginatedState.userDecrypted$;
  }

  // ============================================================================
  // Updater aliases
  // ============================================================================

  public readonly setWorkspaceId = (workspaceId: string | undefined) => this.paginatedState.setWorkspaceId(workspaceId);
  public readonly setCurrentUserId = (currentUserId: string | undefined) =>
    this.paginatedState.setCurrentUserId(currentUserId);
  public readonly setSortCriteria = (sortCriteria: CaseSortCriteria) =>
    this.paginatedState.setSortCriteria(sortCriteria);
  public readonly addFilter = (filterItem: CaseListFilter) => this.paginatedState.addFilter(filterItem);
  public readonly removeFilter = (field: string) => this.paginatedState.removeFilter(field);
  public readonly resetFiltersAndSort = () => this.paginatedState.resetFiltersAndSort();
  public readonly setCanDecrypt = (canDecrypt: boolean) => this.paginatedState.setCanDecrypt(canDecrypt);
  public readonly setEncrypted = (encrypted: boolean) => this.paginatedState.setEncrypted(encrypted);
  public readonly setNameEncrypted = (nameEncrypted: boolean) => this.paginatedState.setNameEncrypted(nameEncrypted);
  public readonly setOrganizationId = (organizationId: string | undefined) =>
    this.paginatedState.setOrganizationId(organizationId);
  public readonly setUserDecrypted = (userDecrypted: boolean) => this.paginatedState.setUserDecrypted(userDecrypted);
  public readonly setSearchTerm = (searchTerm: string) => this.paginatedState.setSearchTerm(searchTerm);
  public readonly changePageSize = (pageSize: number) => this.paginatedState.changePageSize(pageSize);
  public readonly setInitialPagination = (params: {
    pageSize?: number;
    previousTokens?: (WorkspaceCaseData | undefined)[];
  }) => this.paginatedState.setInitialPagination(params);

  // ============================================================================
  // Custom effects
  // ============================================================================

  public readonly initializeCases = this.effect((action$: Observable<string | undefined>) =>
    action$.pipe(
      filter((workspaceId): workspaceId is string => workspaceId !== undefined),
      concatLatestFrom(() => [this.paginatedState.initialPageSize$, this.paginatedState.initialPreviousTokens$]),
      tap(([, initialPageSize, initialPreviousTokens]) => {
        if (initialPageSize || initialPreviousTokens) {
          const lastItem = initialPreviousTokens?.length
            ? initialPreviousTokens[initialPreviousTokens.length - 1]
            : undefined;

          this.initializeItemsAt({
            lastItem,
            pageSize: initialPageSize,
            previousTokens: initialPreviousTokens,
          });
          this.paginatedState.clearInitialPagination();
        } else {
          this.initializeItems();
        }
      })
    )
  );

  private readonly onSortOrFilterChange = this.effect((trigger$: Observable<[CaseSortCriteria, CaseListFilter[]]>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.paginatedState.workspaceId$),
      filter(([, workspaceId]) => workspaceId !== undefined),
      concatLatestFrom(() => this.paginatedState.searchTerm$),
      tap(([, searchTerm]) => {
        this.paginatedState.resetItemsForReload();
        this.countItems();
        this.listItems({ searchTerm: searchTerm, lastItem: undefined });
      })
    )
  );

  public readonly moveCase = this.effect((input$: Observable<{ caseId: string; destinationWorkspaceId: string }>) =>
    input$.pipe(
      tap(() => {
        this.paginatedState.setMovingCase(true);
        this.paginatedState.setOperationError(undefined);
      }),
      switchMap(({ caseId, destinationWorkspaceId }) =>
        from(this.moveCasesHandler.execute(new MoveCases({ caseId, workspaceId: destinationWorkspaceId }))).pipe(
          tap(() => {
            this.paginatedState.setMovingCase(false);
            this.initializeItems();
            // Emit AFTER local list reset so listeners see a settled state.
            this._caseOperationCompleted$.next();
          }),
          catchError(() => {
            this.paginatedState.setMovingCase(false);
            this.paginatedState.setOperationError('workspaces.cases.moving.failed');
            return of(null);
          })
        )
      )
    )
  );

  public readonly copyCase = this.effect((input$: Observable<{ caseId: string; destinationWorkspaceId: string }>) =>
    input$.pipe(
      tap(() => {
        this.paginatedState.setCopyingCase(true);
        this.paginatedState.setOperationError(undefined);
      }),
      switchMap(({ caseId, destinationWorkspaceId }) =>
        from(this.copyCasesHandler.execute(new CopyCases({ caseId, workspaceId: destinationWorkspaceId }))).pipe(
          tap(() => {
            this.paginatedState.setCopyingCase(false);
            this.initializeItems();
            // Emit AFTER local list reset so listeners see a settled state.
            this._caseOperationCompleted$.next();
          }),
          catchError((error) => {
            console.error(error);
            this.paginatedState.setCopyingCase(false);
            this.paginatedState.setOperationError('workspaces.cases.copying.failed');
            return of(null);
          })
        )
      )
    )
  );

  /**
   * Override the post-deletion hook from PaginatedStateEffects.
   * The base class only invokes this when deleteRepositoryItem returns `true`,
   * so emitting here is a clean success signal for the deletion path.
   */
  protected override handleItemAfterDatasetRemoval(itemId: string): void {
    super.handleItemAfterDatasetRemoval(itemId);
    this._caseOperationCompleted$.next();
  }

  private readonly encryptCases = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.paginatedState.workspaceId$),
      filter(([, workspaceId]) => workspaceId !== undefined),
      tap(() => {
        this.relistPage();
        this.paginatedState.setDecryptingCases(false);
      })
    )
  );

  private performDecryption(caseIds: string[]): Observable<unknown[] | null> {
    return from(CloudFunctions.decryptCases(caseIds, false)).pipe(
      tap((decryptedCases) => {
        decryptedCases.forEach((c) => {
          this.paginatedState.updateCaseFields({ id: c.id, changes: { name: c.name, encrypted: false } });
        });
      }),
      catchError(() => {
        this.paginatedState.setOperationError('workspaces.cases.decryption.decrypting_failed');
        return of(null);
      })
    );
  }

  private readonly decryptCases = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.paginatedState.setOperationError(undefined);
        this.paginatedState.setDecryptingCases(true);
      }),
      concatLatestFrom(() => this.paginatedState.cases$),
      switchMap(([, cases]) => {
        const caseIds = cases.filter((c) => c.encrypted).map((c) => c.id);

        if (caseIds.length === 0) {
          this.paginatedState.setDecryptingCases(false);
          return of(null);
        }

        return this.performDecryption(caseIds).pipe(tap(() => this.paginatedState.setDecryptingCases(false)));
      })
    )
  );

  public readonly toggleDecryption = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.paginatedState.alwaysDecrypted$, this.paginatedState.canDecrypt$]),
      // An always-decrypted page makes the toggle a no-op — bail out BEFORE the permission check
      // so a creator-owned workspace never surfaces a spurious permission error.
      filter(([, alwaysDecrypted]) => !alwaysDecrypted),
      tap(([, , canDecrypt]) => {
        if (!canDecrypt) {
          this.paginatedState.setOperationError('workspaces.cases.decryption.permission_denied');
        }
      }),
      filter(([, , canDecrypt]) => canDecrypt),
      tap(() => this.paginatedState.setDecryptingCases(true)),
      concatLatestFrom(() => this.paginatedState.decrypted$),
      switchMap(([, decrypted]) => {
        if (decrypted) {
          this.paginatedState.setUserDecrypted(false);
          this.encryptCases(of(undefined));
        } else {
          this.paginatedState.setUserDecrypted(true);
          this.decryptCases(of(undefined));
        }
        return of(null);
      })
    )
  );

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected override async countRepository(searchTerm?: string): Promise<number> {
    // `defaultValue` keeps the destroyed-store case (selectors complete without
    // emitting) resolving `undefined` instead of rejecting with EmptyError.
    const snapshot = await firstValueFrom(
      combineLatest([
        this.paginatedState.workspaceId$,
        this.paginatedState.nameEncrypted$,
        this.paginatedState.organizationId$,
      ]),
      { defaultValue: undefined }
    );
    if (!snapshot) return 0;
    const [workspaceId, nameEncrypted, organizationId] = snapshot;
    if (!workspaceId) return 0;
    const searchContext = nameEncrypted && organizationId ? { encrypted: nameEncrypted, organizationId } : undefined;
    const count = await this.countWorkspaceCasesQuery.execute(workspaceId, searchTerm, searchContext);
    return count ?? 0;
  }

  protected override listFlow(
    limit: number,
    lastItem: WorkspaceCaseData | undefined,
    searchTerm: string | undefined
  ): Observable<WorkspaceCaseData[]> {
    return combineLatest([
      this.paginatedState.workspaceId$,
      this.paginatedState.sortCriteria$,
      this.paginatedState.filters$,
      this.paginatedState.encrypted$,
      this.paginatedState.nameEncrypted$,
      this.paginatedState.organizationId$,
      this.paginatedState.currentUserId$,
    ]).pipe(
      take(1),
      switchMap(([workspaceId, sortCriteria, filters, wsEncrypted, nameEncrypted, organizationId, currentUserId]) => {
        if (!workspaceId) return of([]);
        const searchContext: CaseSearchContext | undefined =
          nameEncrypted && organizationId ? { encrypted: nameEncrypted, organizationId } : undefined;
        return from(
          this.listWorkspaceCasesQuery.execute({
            workspaceId,
            limit,
            lastItem: lastItem ? { id: lastItem.id, name: lastItem.name, createdAt: lastItem.createdAt } : undefined,
            searchTerm: searchTerm || undefined,
            sorts: Object.keys(sortCriteria).length > 0 ? sortCriteria : undefined,
            filters: filters.length > 0 ? filters : undefined,
            searchContext,
          })
        ).pipe(
          filter((cases): cases is ListedWorkspaceCase[] => cases !== null),
          map((cases: ListedWorkspaceCase[]) =>
            cases.map((c) => ({
              id: c.id,
              name: c.name as string,
              encrypted: wsEncrypted,
              createdAt: c.createdAt,
              updatedAt: c.updatedAt,
              createdById: c.createdById,
            }))
          ),
          switchMap((cases) => {
            if (!wsEncrypted || !currentUserId) return of(cases);

            // Creator-owned cases are returned already decrypted by the backend — just flip the flag.
            const creatorCaseIds = cases.filter((c) => c.createdById === currentUserId).map((c) => c.id);
            this.paginatedState.setAlwaysDecrypted(cases.length > 0 && cases.length === creatorCaseIds.length);

            const casesWithCreatorDecrypted = cases.map((c) =>
              c.createdById === currentUserId ? { ...c, encrypted: false } : c
            );

            // If the user had previously triggered full decryption, decrypt the remaining non-creator cases.
            // Deliberately a late snapshot: the user may toggle decryption while the list request is in flight.
            return this.paginatedState.userDecrypted$.pipe(
              take(1),
              switchMap((userDecrypted) => {
                if (!userDecrypted) return of(casesWithCreatorDecrypted);

                const stillEncryptedIds = casesWithCreatorDecrypted.filter((c) => c.encrypted).map((c) => c.id);
                if (stillEncryptedIds.length === 0) return of(casesWithCreatorDecrypted);

                return from(CloudFunctions.decryptCases(stillEncryptedIds, false)).pipe(
                  map((decrypted) => {
                    const decryptedMap = new Map<string, string>(decrypted.map((d) => [d.id, d.name as string]));
                    return casesWithCreatorDecrypted.map((c) => {
                      const decryptedName = decryptedMap.get(c.id);
                      return decryptedName !== undefined ? { ...c, name: decryptedName, encrypted: false } : c;
                    });
                  }),
                  catchError(() => of(casesWithCreatorDecrypted))
                );
              })
            );
          })
        );
      })
    );
  }

  protected override async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.caseRepository.delete(itemId);
      return true;
    } catch {
      this.paginatedState.setOperationError('workspaces.cases.deletion.failed');
      return false;
    }
  }

  protected override processDeferredAttributes(itemId: string): Observable<Partial<WorkspaceCaseData>> {
    return combineLatest([this.paginatedState.currentUserId$, this.paginatedState.cases$]).pipe(
      take(1),
      switchMap(([currentUserId, cases]) => {
        if (!currentUserId) return EMPTY;

        const item = cases.find((c) => c.id === itemId);
        if (!item) return EMPTY;

        return from(
          this.analysisSummaryQuery.execute({
            caseId: itemId,
            currentUserId,
            createdById: item.createdById,
          })
        ).pipe(
          map((summary) => ({
            state: summary.state as WorkspaceCaseState,
            createdBy: summary.createdBy,
            analysing: summary.analysing,
            analyzed: summary.analyzed,
          })),
          this.carefulErrors()
        );
      })
    );
  }
}
